import AbstractService from '../abstract-service';
import AddToCartParametersInterface from '../../vo/event/add-to-cart-parameters-interface';
import CheckoutParametersInterface from '../../vo/event/checkout-parameters-interface';
import ClickEventParametersInterface from '../../vo/event/click-event-parameters-interface';
import ConversionParametersInterface from '../../vo/event/conversion-parameters-interface';
import EventParametersInterface from '../../vo/event/event-parameters-interface';
import GoogleConfigurationDto from '../../vo/google/google-configuration-dto';
import GoogleConfigurationInterface from '../../vo/google/google-configuration-interface';
import GoogleConfigurationParametersInterface from '../../vo/google/google-configuration-parameters-interface';
import GoogleEventParametersInterface from '../../vo/google/google-event-parameters-interface';
import ItemParametersInterface from '../../vo/event/item-parameters-interface';
import ItemViewParametersInterface from '../../vo/event/item-view-parameters-interface';
import OrderParametersInterface from '../../vo/event/order-parameters-interface';
import PageViewParametersInterface from '../../vo/event/page-view-parameters-interface';
import ServiceInterface from '../service-interface';
import SubmitEventParametersInterface from '../../vo/event/submit-event-parameters-interface';

declare global
{
    interface Window
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataLayer: any;
    }
}

const gtag: Gtag.Gtag = function()
{
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
};

export default class GoogleAnalytics extends AbstractService implements ServiceInterface
{
    protected configuration: GoogleConfigurationInterface;

    public constructor(configuration: GoogleConfigurationInterface)
    {
        super(configuration);

        this.configuration = Object.assign(new GoogleConfigurationDto(), configuration);
    }

    public define(): void
    {
        if (this.defined)
        {
            return;
        }

        if (this.configuration.useFirstParty)
        {
            this.addScript(`/metrics/gtag/js?id=${ this.configuration.key }`, true);
        }
        else
        {
            this.addScript(`https://www.googletagmanager.com/gtag/js?id=${ this.configuration.key }`, true);
        }

        window.dataLayer = window.dataLayer || [];

        gtag('js', new Date());

        gtag(
            'consent', 'default', this.consentParameters,
        );

        gtag(
            'config', this.configuration.key, this.configParameters,
        );

        if (this.configuration.adwordsId)
        {
            gtag('config', `AW-${ this.configuration.adwordsId }`);
        }

        if (this.configuration.useFirstParty)
        {
            gtag('set','transport_url',window.location.origin + '/metrics/');
        }

        this.setDefined();
    }

    protected get consentParameters(): Gtag.ConsentParams
    {
        const consentParameters: Gtag.ConsentParams = {};

        if (this.configuration.consent === false)
        {
            consentParameters.ad_storage = 'denied';
            consentParameters.analytics_storage = 'denied';
            consentParameters.ad_user_data = 'denied';
        }
        else
        {
            consentParameters.ad_storage = 'granted';
            consentParameters.analytics_storage = 'granted';
            consentParameters.ad_user_data = 'granted';
        }


        if (this.configuration.personalization === false)
        {
            consentParameters.ad_personalization = 'denied';
        }
        else
        {
            consentParameters.ad_personalization = 'granted';
        }

        return consentParameters;
    }

    protected get configParameters(): GoogleConfigurationParametersInterface
    {
        const configParameters: GoogleConfigurationParametersInterface = {
            send_page_view: false,
        };

        if (this.configuration.userId)
        {
            configParameters.user_id = this.configuration.userId;
        }

        if (this.configuration.debug)
        {
            configParameters.debug_mode = true;
        }

        if (this.configuration.useBeacon)
        {
            configParameters.transport_type = 'beacon';
        }

        if (this.configuration.contentGroup)
        {
            configParameters.content_group = this.configuration.contentGroup;
        }

        return configParameters;
    }

    public trackEvent(event: string, parameters: EventParametersInterface): void
    {
        const googleParameters: GoogleEventParametersInterface = this.getEventParameters(parameters);

        gtag(
            'event', event, googleParameters,
        );
    }

    public trackPageView(parameters: PageViewParametersInterface): void
    {
        if (!parameters.key)
        {
            parameters.key = this.configuration.key;
        }

        this.trackEvent('page_view', parameters);
    }

    public trackAddToCart(parameters: AddToCartParametersInterface): void
    {
        this.trackEvent('add_to_cart', parameters);
    }

    public trackRemoveFromCart(parameters: AddToCartParametersInterface): void
    {
        this.trackEvent('remove_from_cart', parameters);
    }

    public trackCheckout(parameters: CheckoutParametersInterface): void
    {
        switch(parameters.checkoutStep)
        {
            case 1:
                return this.trackEvent('view_cart', parameters);
            case 5:
                return this.trackEvent('add_shipping_info', parameters);
            case 6:
                return this.trackEvent('add_payment_info', parameters);
        }
    }

    public trackClick(parameters: ClickEventParametersInterface): void
    {
        this.trackEvent('click', parameters);
    }

    public trackSubmission(parameters: SubmitEventParametersInterface): void
    {
        this.trackEvent('submit', parameters);
    }

    protected getEventParameters(parameters: EventParametersInterface): GoogleEventParametersInterface
    {
        const eventParameters: GoogleEventParametersInterface = {
            affiliation: parameters.site ?? undefined,
            checkout_option: parameters.checkoutOption ?? undefined,
            checkout_step: parameters.checkoutStep ?? undefined,
            coupon: parameters.voucher ?? undefined,
            currency: parameters.currency ?? undefined,
            description: parameters.description ?? undefined,
            event_category: parameters.category ?? undefined,
            event_label: parameters.label ?? undefined,
            id: parameters.id ?? undefined,
            method: parameters.method ?? undefined,
            non_interaction: parameters.nonInteraction ?? undefined,
            page_location: parameters.pageLocation ?? undefined,
            page_path: parameters.pagePath ?? undefined,
            page_title: parameters.pageTitle ?? undefined,
            send_to: parameters.key ?? undefined,
            shipping: parameters.shipping ?? undefined,
            shipping_tier: parameters.shippingService ?? undefined,
            tax: parameters.vat ?? undefined,
            transaction_id: parameters.transactionId ?? undefined,
            value: parameters.value ?? undefined,
        };

        if (parameters.items)
        {
            const items: Gtag.Item[] = [];

            for (const item of parameters.items)
            {
                items.push(this.orderItemToParameters(item));
            }

            eventParameters.items = items;
        }

        return this.filterParameters(eventParameters);
    }

    public trackOrder(order: OrderParametersInterface): void
    {
        this.trackEvent('purchase', order);

        if (this.configuration.adwordsId && this.configuration.conversionLabel)
        {
            this.trackConversion(order);
        }
    }

    protected orderItemToParameters(item: ItemParametersInterface): Gtag.Item
    {
        const itemParameters: Gtag.Item = {
            item_id: item.id ?? undefined,
            item_name: item.title ?? undefined,
            price: item.price ?? undefined,
            discount: item.discount ?? undefined,
            quantity: item.quantity ?? undefined,
            item_category: item.category ?? undefined,
            item_brand: item.brand ?? undefined,
            item_variant: item.variation ?? undefined,
        };

        return this.filterParameters(itemParameters);
    }

    public trackItemView(parameters: ItemViewParametersInterface): void
    {
        this.trackEvent('view_item', parameters);
    }

    public trackConversion(order: OrderParametersInterface): void
    {
        const conversionParameters: ConversionParametersInterface = {
            key: `AW-${ this.configuration.adwordsId }/${ this.configuration.conversionLabel }`,
            value: order.value,
            currency: order.currency,
            transactionId: order.transactionId,
        };

        this.trackEvent('conversion', conversionParameters);
    }

    public trackAddToWishlist(parameters: ItemViewParametersInterface): void
    {
        this.trackEvent('add_to_wishlist', parameters);
    }
}